import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { ThemeKey } from 'types/general.types';

import ThemesData from './themes.json';

export enum LogoID {
  terahash = 'terahash',
  duckEnergy = 'duckEnergy',
}
export enum ImageID {
  euro = 'euro',
  costs = 'costs',
  btc = 'btc',
  estimate = 'estimate',
}

export type Theme = {
  fontFamily: string;
  headerColor: string;
  headerTextColor: string;
  footerColor: string;
  footerTextColor: string;
  background: LogoID;
  indicatorColor: string;
  loader: string;
  contactSupportLink?: string;
  buttons: {
    primary: {
      color: string;
      textColor: string;
      hoverColor: string;
      hoverText: string;
    };
    secondary: {
      color: string;
      textColor: string;
      hoverColor: string;
      hoverText: string;
    };
  };
  logo: {
    imageId: LogoID;
    width: string;
    height: string;
  };
  widgets: {
    fiatWallet: { imageId: ImageID; textColor: string };
    dailyProfit: { imageId: ImageID; textColor: string };
    bitcoinWallet: { imageId: ImageID; textColor: string };
    dailyPayout: { imageId: ImageID; textColor: string };
  };
  charts: {
    v1: {
      color: string;
      bgColor: string;
      lineColor?: string;
    };
    v2: {
      color: string;
      bgColor: string;
      lineColor?: string;
    };
  };
  // Server-driven white-label extras (tenant_branding). Optional: bundled themes.json
  // entries don't set them.
  logoUrl?: string;
  faviconUrl?: string;
  loginBackgroundUrl?: string;
  displayName?: string;
};

export type Themes = Record<ThemeKey, Theme>;

interface ThemeProviderProps {
  children: ReactNode;
  themeName?: keyof Themes; // Allow a default theme key
  themeOverride?: Theme; // Server-driven theme (tenant_branding) takes precedence
}

const themes: Themes = ThemesData as Themes;

const ThemeContext = createContext<{
  theme: Theme;
  themeKey: ThemeKey;
  setThemeKey: (key: keyof Themes) => void;
} | null>(null);

export const getBaseTheme = (key: ThemeKey): Theme => themes[key] ?? themes[ThemeKey.DUCKENERGY];

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  themeName = ThemeKey.DUCKENERGY,
  themeOverride,
}) => {
  const [themeKey, setThemeKey] = useState<keyof Themes>(themeName);
  const theme = themeOverride ?? getBaseTheme(themeKey);

  useEffect(() => {
    if (!themes[themeKey]) {
      console.warn(`Theme with key "${themeKey}" does not exist; falling back to the default theme.`);
      setThemeKey(ThemeKey.DUCKENERGY);
    }
  }, [themeKey]);

  const value = useMemo(() => ({ themeKey, theme, setThemeKey }), [themeKey, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
